/**
 * Minimal fetch wrapper for the VeriDoc backend.
 *
 * All requests go to `/vd/*`, which Vite dev server proxies to
 * `http://localhost:8000/vd/*` (see vite.config.ts). In production, the
 * frontend is served from the same origin as the API, so the same path
 * works without changes.
 *
 * Phase 1.1 scope: just enough to demonstrate the proxy contract. Real
 * typed clients (openapi-typescript codegen) land in a later phase.
 */

/** Standard envelope shared by all backend responses (per ADR-101). */
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
  meta?: Record<string, unknown>;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  /** Structured context from the flat envelope's ``details`` field (T21). */
  readonly details: Record<string, unknown> | undefined;
  /** Correlation id from ``request_id`` — pairs with the X-Request-ID response header (T19/T21). */
  readonly requestId: string | undefined;

  constructor(
    message: string,
    status: number,
    code?: string,
    details?: Record<string, unknown>,
    requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }
}

const DEFAULT_BASE_PATH = '/vd';
const CSRF_COOKIE = 'vd_csrf';
const CSRF_HEADER = 'X-CSRF-Token';
const WRITE_METHODS: ReadonlySet<string> = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** Subset of the Fetch API credentials option relevant to VeriDoc hosts. */
export type CredentialsMode = 'include' | 'same-origin' | 'omit';

/**
 * Transport seam (ADR-119 D2). The component library reaches the backend
 * through a runtime-configurable singleton so a host can point it at their own
 * mount point and supply their own transport (auth, headers) without editing
 * library code. The demo leaves the defaults — same-origin `/vd` + cookie
 * credentials — so behaviour is unchanged until a host calls
 * `configureVeriDocClient`.
 *
 *   configureVeriDocClient({ baseUrl })       → host mounts VeriDoc elsewhere
 *   configureVeriDocClient({ fetch })         → host wraps fetch to inject auth
 *   resetVeriDocClient()                       → test isolation (beforeEach)
 *
 * `configureVeriDocClient` MUST run before the first request — i.e. before any
 * component that calls the API mounts (typically in the app entry, ahead of
 * `createRoot().render()`). See docs/research R3a design §integration notes.
 */
interface VeriDocClientConfig {
  /** Base path/URL every request is prefixed with. No trailing slash. */
  baseUrl: string;
  /** Transport. Host can wrap to inject Authorization headers, etc. */
  fetch: typeof fetch;
  /**
   * Fetch credentials mode. Default: 'include' (same-origin cookie — current
   * demo behaviour). Bearer / cross-origin hosts use 'omit' and inject
   * Authorization in their fetch wrapper instead.
   */
  credentials: CredentialsMode;
  /**
   * Whether to inject a cookie-based CSRF header (X-CSRF-Token from the
   * vd_csrf cookie) on write requests. Default: true (demo behaviour).
   * Set to false when the host has no vd_csrf cookie (e.g. bearer auth).
   */
  csrf: boolean;
}

function defaultConfig(): VeriDocClientConfig {
  return {
    baseUrl: DEFAULT_BASE_PATH,
    // Bind so a host-overridden global fetch is still honoured at call time.
    fetch: (input: RequestInfo | URL, init?: RequestInit) =>
      globalThis.fetch(input, init),
    credentials: 'include',
    csrf: true,
  };
}

let clientConfig: VeriDocClientConfig = defaultConfig();

function stripTrailingSlash(value: string): string {
  return value.endsWith('/') ? value.replace(/\/+$/, '') : value;
}

/** Configure the transport seam. Merges over current config (partial). */
export function configureVeriDocClient(
  next: Partial<VeriDocClientConfig>,
): void {
  clientConfig = {
    baseUrl:
      next.baseUrl !== undefined
        ? stripTrailingSlash(next.baseUrl)
        : clientConfig.baseUrl,
    fetch: next.fetch ?? clientConfig.fetch,
    credentials: next.credentials ?? clientConfig.credentials,
    csrf: next.csrf !== undefined ? next.csrf : clientConfig.csrf,
  };
}

/** Reset the transport seam to defaults. For test isolation. */
export function resetVeriDocClient(): void {
  clientConfig = defaultConfig();
}

/**
 * The configured base path. Exposed so non-fetch endpoints (e.g. the export
 * download URL the browser navigates to directly) resolve against the same
 * mount point as `fetch` requests, instead of a hardcoded `/vd`.
 */
export function veridocBaseUrl(): string {
  return clientConfig.baseUrl;
}

function resolveUrl(path: string): string {
  const base = clientConfig.baseUrl;
  return path.startsWith('/') ? `${base}${path}` : `${base}/${path}`;
}

/**
 * Read a cookie value by name. Returns undefined when missing — used to
 * source the ``vd_csrf`` token for the three-way CSRF compare (ADR-108
 * D3 / SR3 in docs/saas-security-requirements.md). The cookie is set
 * non-HttpOnly precisely so the SPA can copy it into ``X-CSRF-Token``.
 */
function readCookie(name: string): string | undefined {
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }
  return undefined;
}

function csrfHeaderFor(method: string): Record<string, string> {
  if (!WRITE_METHODS.has(method.toUpperCase())) return {};
  const token = readCookie(CSRF_COOKIE);
  return token ? { [CSRF_HEADER]: token } : {};
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** Will be JSON.stringified automatically. Pass undefined to omit body. */
  json?: unknown;
}

/**
 * Extract the flat error envelope fields from a response body.
 *
 * T21 unified the backend error shape to:
 *
 *   { code, message, details, request_id }
 *
 * Older call sites that pre-date T21 emit ``{detail: {code, message}}``
 * (FastAPI's HTTPException default). We read the flat shape first and
 * fall back to the legacy ``detail`` shape for one iteration so a
 * half-deployed backend (server upgraded, host project not yet) keeps
 * working. The fallback is removed once every consumer is on T21+.
 */
interface ParsedErrorBody {
  code?: string;
  message?: string;
  details?: Record<string, unknown>;
  requestId?: string;
}

async function readErrorBody(response: Response): Promise<ParsedErrorBody> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return {};
  }
  if (!body || typeof body !== 'object') {
    return {};
  }
  const obj = body as Record<string, unknown>;
  // Flat T21 shape — the dominant path going forward.
  const flatCode = typeof obj.code === 'string' ? obj.code : undefined;
  const flatMessage = typeof obj.message === 'string' ? obj.message : undefined;
  const flatDetails =
    obj.details && typeof obj.details === 'object'
      ? (obj.details as Record<string, unknown>)
      : undefined;
  const flatRequestId =
    typeof obj.request_id === 'string' ? obj.request_id : undefined;
  if (flatCode || flatMessage) {
    return {
      code: flatCode,
      message: flatMessage,
      details: flatDetails,
      requestId: flatRequestId,
    };
  }
  // Legacy ``{detail: {code, message}}`` fallback (one-iteration compat).
  // P3-17: emit a console.warn so a host that keeps hitting this branch is
  // visible in dev tools — without it, a half-upgraded backend silently
  // degrades the error contract and the only signal is missing fields.
  const detail = obj.detail;
  if (detail && typeof detail === 'object') {
    if (typeof console !== 'undefined' && typeof console.warn === 'function') {
      console.warn(
        '[veridoc] readErrorBody: legacy `{detail:{code,message}}` fallback in use — backend should emit the flat T21 envelope.',
      );
    }
    const d = detail as { code?: unknown; message?: unknown };
    return {
      code: typeof d.code === 'string' ? d.code : undefined,
      message: typeof d.message === 'string' ? d.message : undefined,
    };
  }
  if (typeof detail === 'string') {
    if (typeof console !== 'undefined' && typeof console.warn === 'function') {
      console.warn(
        '[veridoc] readErrorBody: legacy `{detail: <string>}` fallback in use.',
      );
    }
    return { message: detail };
  }
  // Legacy ADR-101 envelope ``{error: {code, message}}``.
  const error = (obj as { error?: { code?: string; message?: string } }).error;
  if (error) {
    if (typeof console !== 'undefined' && typeof console.warn === 'function') {
      console.warn(
        '[veridoc] readErrorBody: legacy `{error:{code,message}}` ADR-101 envelope fallback in use.',
      );
    }
    return { code: error.code, message: error.message };
  }
  return {};
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { json, headers, method = 'GET', ...rest } = options;

  const init: RequestInit = {
    ...rest,
    method,
    credentials: clientConfig.credentials,
    headers: {
      Accept: 'application/json',
      ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(clientConfig.csrf ? csrfHeaderFor(method) : {}),
      ...headers,
    },
    ...(json !== undefined ? { body: JSON.stringify(json) } : {}),
  };

  const url = resolveUrl(path);

  let response: Response;
  try {
    response = await clientConfig.fetch(url, init);
  } catch (err) {
    throw new ApiError(
      `Network request failed: ${err instanceof Error ? err.message : String(err)}`,
      0,
    );
  }

  if (!response.ok) {
    const { code, message, details, requestId } = await readErrorBody(response);
    throw new ApiError(
      message ?? `HTTP ${response.status}`,
      response.status,
      code,
      details,
      requestId,
    );
  }

  // 204 No Content / 205 Reset Content — no body to parse.
  if (response.status === 204 || response.status === 205) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

/**
 * POST a multipart/form-data body. Used by the upload endpoint so we can
 * stream files without buffering them as base64 in JSON.
 *
 * Browsers set the multipart boundary header automatically when the body is
 * a `FormData`, so we explicitly omit `Content-Type` to avoid clobbering it.
 */
async function postFormData<T>(path: string, formData: FormData): Promise<T> {
  const url = resolveUrl(path);

  let response: Response;
  try {
    response = await clientConfig.fetch(url, {
      method: 'POST',
      body: formData,
      credentials: clientConfig.credentials,
      headers: {
        Accept: 'application/json',
        ...(clientConfig.csrf ? csrfHeaderFor('POST') : {}),
      },
    });
  } catch (err) {
    throw new ApiError(
      `Network request failed: ${err instanceof Error ? err.message : String(err)}`,
      0,
    );
  }

  if (!response.ok) {
    const { code, message, details, requestId } = await readErrorBody(response);
    throw new ApiError(
      message ?? `HTTP ${response.status}`,
      response.status,
      code,
      details,
      requestId,
    );
  }

  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'json'>) =>
    request<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, json?: unknown, options?: Omit<RequestOptions, 'method' | 'json'>) =>
    request<T>(path, { ...options, method: 'POST', json }),

  put: <T>(path: string, json?: unknown, options?: Omit<RequestOptions, 'method' | 'json'>) =>
    request<T>(path, { ...options, method: 'PUT', json }),

  patch: <T>(path: string, json?: unknown, options?: Omit<RequestOptions, 'method' | 'json'>) =>
    request<T>(path, { ...options, method: 'PATCH', json }),

  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'json'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),

  postFormData: <T>(path: string, formData: FormData) => postFormData<T>(path, formData),
};
